<script setup>
import ApplePay from '#common/components/Payment/ApplePay.vue'
import PayPal from '#common/components/Payment/PayPal.vue'
import WithHostedFields from '#common/components/Payment/WithHostedFields.vue'
import { pickValueFromPlace } from '#common/utils/address'
import { scrollToElement } from '#common/utils/form'
import { isApplePaySupported } from '#common/utils/payment'

const emit = defineEmits([
  'payPalTokenized',
  'applePayTokenized',
  'ccTokenized',
  'savePaymentDetailsClicked',
  'billingSameAsShippingToggled',
])

const props = defineProps({
  hostedFieldsStyles: {
    type: Object,
    required: false,
    default() {
      return null
    },
  },
  omitHostedFieldsStyles: {
    type: Boolean,
    default: false,
  },
  showPayPalButton: {
    type: Boolean,
    default: true,
  },
  amount: {
    type: Number,
    required: false,
    default: 0,
  },
  forceDefault: {
    type: Boolean,
    default: true,
  },
  initial: {
    type: Object,
    default() {
      return {}
    },
  },
  disabled: {
    type: Boolean,
  },
  submit: {
    type: Function,
    default: () => {},
  },
  includeNumber: {
    type: Boolean,
    default: false,
  },
  saveError: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: 'payment-method-form',
  },
  formId: {
    type: String,
    default: 'payment-method-form',
  },
  hideButtons: {
    type: Boolean,
    default: false,
  },
  customTheme: {
    type: Object,
    required: false,
    default() {
      return {
        showPlaceholders: true,
        cardholderName: {
          classes: '',
        },
        billingSameAsShippingCheckbox: {
          classes: 'checkbox-group',
        },
        savePaymentDetailsCheckbox: {
          classes: 'checkbox-group',
        },
        makeDefaultCheckbox: {
          classes: 'checkbox-group',
        },
        billingAddress: {
          line1: {
            classes: '',
          },
          line2: {
            classes: '',
          },
          city: {
            classes: 'medium-4',
          },
          state: {
            classes: 'medium-4',
          },
          zip: {
            classes: 'medium-4',
          },
        },
      }
    },
  },
  showBillingSameAsShippingCheckbox: {
    type: Boolean,
    default: true,
  },
  showSavePaymentDetailsCheckbox: {
    type: Boolean,
    default: false,
  },
  savePaymentDetailsDefault: {
    type: Boolean,
    default: true,
  },
  showMakeDefaultCheckbox: {
    type: Boolean,
    default: true,
  },
  shippingAddress: {
    type: Object,
    required: false,
    default: null,
  },
  cancelHref: {
    type: String,
    default: '/',
  },
  submitButtonClass: {
    type: String,
    default: 'button button-solid',
  },
  submitButtonText: {
    type: String,
    default: 'Save Changes',
  },
  hideCancel: {
    type: Boolean,
    default: false,
  },
  addressLineOneId: {
    type: String,
    default: 'billing1',
  },
})

const initialFormValues = _.cloneDeep(props.initial)
initialFormValues.billing1 = props.initial?.billing_address?.billing1 || ''
initialFormValues.billing2 = props.initial?.billing_address?.billing2 || ''
initialFormValues.city = props.initial?.billing_address?.city || ''
initialFormValues.state = props.initial?.billing_address?.state || ''
initialFormValues.zipcode = props.initial?.billing_address?.zipcode || ''

const braintreeSpecificDomReady = ref(false)
const formValues = ref(initialFormValues)
const billingSameAsShipping = ref(false)
const savePaymentDetails = ref(props.savePaymentDetailsDefault)
const makeDefault = ref(props.forceDefault)
const tokenizeErrors = ref({})
const creditCardForm = ref(null)
const submitting = ref(false)
const theme = ref(props.customTheme)
const enableApplePay = ref(false)
const applePayButtonStyle = ref({
  buttonstyle: 'white-outline',
  type: 'plain',
  buttonWidth: '100%',
  buttonHeight: '42px',
})
const metadata = useMetadata()
const stateNamesToCodes = computed(() => metadata.stateNamesToCodes)
const stateOptions = computed(() => metadata.stateCodesToNames)

const showMakeDefaultCheckbox_ = computed(() => {
  if (!savePaymentDetails.value) {
    return false
  } else {
    return props.showMakeDefaultCheckbox
  }
})
const getHostedFieldsStyles = computed(() => {
  if (props.omitHostedFieldsStyles) {
    return null
  }
  if (props.hostedFieldsStyles === null) {
    return {
      'input, select': {
        color: 'rgba(0, 0, 0, 0.45)',
        'font-size': '12.5px',
        'font-family': 'europa, sans-serif',
        padding: '0.3125rem 0',
        '-webkit-appearance': 'none',
        outline: 'none',
      },
      '.invalid': {
        color: 'red',
      },
      '.valid': {
        color: 'inherit',
      },
      ':focus': {
        color: 'inherit',
      },
    }
  }
  return props.hostedFieldsStyles
})

function payPalTokenized(payload) {
  const apiData = {
    token: payload.nonce,
    details: {
      email: payload.details?.email || null,
    },
    make_default: true,
  }
  emit('payPalTokenized', apiData)
  return apiData
}
function applePayTokenized(payload) {
  const apiData = {
    details: {
      cardholderName: payload.details?.cardholderName || null,
    },
    token: payload.nonce,
    make_default: false,
  }
  emit('applePayTokenized', apiData)
  return apiData
}

async function ccTokenize() {
  const braintree = useBraintree()
  const data = formValues.value
  submitting.value = true

  tokenizeErrors.value = {}
  try {
    const tokenizePayload = await braintree.hostedFieldsInstance.tokenize({
      cardholderName: data.cardholder_name,
      billingAddress: {
        streetAddress: data.billing1,
        extendedAddress: data.billing2,
        locality: data.city,
        region: data.state,
        postalCode: data.zipcode,
      },
    })
    submitting.value = false
    const apiData = {
      make_default: makeDefault.value,
      token: tokenizePayload.nonce,
      type: tokenizePayload.type,
      details: tokenizePayload.details,
    }
    emit('ccTokenized', apiData)
    return apiData
  } catch (e) {
    console.error('error tokenizing fields', e)

    if (e.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
      // Creates an error object with keys corresponding to the
      // fields that have errors
      tokenizeErrors.value = e.details.invalidFieldKeys.reduce(
        (errors, key) => {
          errors[key] = 'Invalid value'
          return errors
        },
        {},
      )
      scrollToElement(nextTick, '#id_card, #id_card_masked')
    } else if (e.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
      tokenizeErrors.value = {
        cvv: 'Invalid value',
        expirationMonth: 'Invalid value',
        expirationYear: 'Invalid value',
      }
      if (!props.initial.masked_number) {
        tokenizeErrors.value.number = 'Invalid value'
      }
      scrollToElement(nextTick, '#id_card, #id_card_masked')
    }
    submitting.value = false
    Sentry.captureException(e)
    return null
  }
}
async function validateAndGetToken() {
  creditCardForm.value.node.submit()
  if (creditCardForm.value.node.context.state.valid) {
    return await ccTokenize()
  }
  return null
}
async function submitForm() {
  if (!props.hideButtons) {
    // if buttons are hidden, we handle ccTokenize from the parent component
    return await ccTokenize()
  }
}
function chNameNotEmail({ value }) {
  return !value.includes('@')
}
function chNameIsFull({ value }) {
  return value.includes(' ')
}
function chNameNotCardType({ value }) {
  const badNames = [
    'visa',
    'mc',
    'mastercard',
    'master card',
    'amex',
    'american express',
    'americanexpress',
    'discover',
  ]
  return !badNames.includes(value)
}
function autoCompletePlaceChanged({ place }) {
  formValues.value.billing1 =
    `${pickValueFromPlace(place, 'street_number').short_name} ${
      pickValueFromPlace(place, 'route').long_name
    }`.trim()
  formValues.value.city = pickValueFromPlace(place, 'locality').long_name

  // Try to use the state short name if it is a valid option
  // otherwise fallback to using the long name, components can
  // use both.
  const state = pickValueFromPlace(place, 'administrative_area_level_1')
  const resolvedState =
    state.short_name in stateOptions.value ? state.short_name : state.long_name
  formValues.value.state = resolvedState
  formValues.value.zipcode = pickValueFromPlace(place, 'postal_code').short_name
}
function clear() {
  formValues.value = _.cloneDeep(props.initial)
  formValues.value.billing1 = props.initial?.billing_address?.billing1 || ''
  formValues.value.billing2 = props.initial?.billing_address?.billing2 || ''
  formValues.value.city = props.initial?.billing_address?.city || ''
  formValues.value.state = props.initial?.billing_address?.state || ''
  formValues.value.zipcode = props.initial?.billing_address?.zipcode || ''
  resetValidation()
}

defineExpose({
  validateAndGetToken,
  clear,
  resetValidation,
})

function resetValidation() {
  creditCardForm.value?.node.reset()
}
// Syncs shipping address to billing address
function syncShippingToBilling(shippingAddr) {
  if (!shippingAddr) {
    console.warn('syncShippingToBilling called with no shippingAddr')
    return
  }
  formValues.value.billing1 = shippingAddr.line1 || ''
  formValues.value.billing2 = shippingAddr.line2 || ''
  formValues.value.city = shippingAddr.line4 || shippingAddr.city || ''
  formValues.value.zipcode = shippingAddr.postcode || ''
  let shippingStateCode = shippingAddr.state_code
  if (!shippingStateCode) {
    shippingStateCode = stateNamesToCodes.value[shippingAddr.state]
  }
  formValues.value.state = shippingStateCode || ''
}
// Checks if billing and shipping addresses differ
function doAddressesDiffer(currentBillingValues, currentShippingAddress) {
  if (!currentShippingAddress) return true
  if (
    !currentShippingAddress.state_code &&
    !currentShippingAddress.state &&
    !currentShippingAddress.line1 &&
    !currentShippingAddress.line2 &&
    !currentShippingAddress.line4 &&
    !currentShippingAddress.city &&
    !currentShippingAddress.postcode
  ) {
    return true
  }
  let shippingStateCode = currentShippingAddress.state_code
  if (!shippingStateCode) {
    shippingStateCode = stateNamesToCodes.value[currentShippingAddress.state]
  }
  const shippingLine1 = currentShippingAddress.line1 || ''
  const shippingLine2 = currentShippingAddress.line2 || ''
  const shippingCity =
    currentShippingAddress.line4 || currentShippingAddress.city || ''
  const shippingPostcode = currentShippingAddress.postcode || ''
  return (
    currentBillingValues.billing1 !== shippingLine1 ||
    currentBillingValues.billing2 !== shippingLine2 ||
    currentBillingValues.city !== shippingCity ||
    currentBillingValues.state !== shippingStateCode ||
    currentBillingValues.zipcode !== shippingPostcode
  )
}
// Watch for changes in billingSameAsShipping and sync shipping to billing if toggled
watch(billingSameAsShipping, (isSame) => {
  if (isSame && props.shippingAddress) {
    syncShippingToBilling(props.shippingAddress)
    emit('billingSameAsShippingToggled', formValues.value)
  }
})
// Watch for changes in formValues and update billingSameAsShipping accordingly
watch(
  formValues,
  (newBillingValues) => {
    if (props.shippingAddress) {
      const areDifferent = doAddressesDiffer(
        newBillingValues,
        props.shippingAddress,
      )

      if (areDifferent) {
        if (billingSameAsShipping.value) {
          billingSameAsShipping.value = false
        }
      } else {
        if (!billingSameAsShipping.value) {
          billingSameAsShipping.value = true
        }
      }
    }
  },
  { deep: true },
)
// Watch for changes in the shipping address prop
watch(
  () => props.shippingAddress,
  (newShippingAddress) => {
    if (billingSameAsShipping.value && newShippingAddress) {
      syncShippingToBilling(newShippingAddress)
    }
  },
  { deep: true },
)
watch(savePaymentDetails, (newValue) => {
  makeDefault.value = newValue
  emit('savePaymentDetailsClicked', newValue)
})

const checkAndSetBraintreeDomReady = async () => {
  await nextTick() // Ensure the DOM updates before checking for elements.

  const fieldIds = ['id_cvv', 'id_exp_month', 'id_exp_year']
  if (props.includeNumber) fieldIds.push('id_card') // Include card field if required.

  // Function to check if all required Braintree elements exist in the DOM.
  const check = () => fieldIds.every((id) => document.getElementById(id))

  // Attempt to confirm DOM readiness within a max retry limit.
  const tryReady = (attempts = 0) => {
    if (check()) {
      braintreeSpecificDomReady.value = true // Mark as ready once elements are found.
      return
    }

    if (attempts < 10) {
      // Retry after a short delay until elements appear or max attempts are reached.
      setTimeout(() => tryReady(attempts + 1), 50)
    } else {
      braintreeSpecificDomReady.value = false // Fallback in case elements never appear.
    }
  }

  tryReady() // Start the readiness check process.
}

onMounted(async () => {
  enableApplePay.value = isApplePaySupported(window)
  await checkAndSetBraintreeDomReady()
})
onActivated(() => {
  braintreeSpecificDomReady.value = false
  checkAndSetBraintreeDomReady()
})
</script>

<template>
  <div>
    <ClientOnly>
      <PayPal
        v-if="showPayPalButton"
        style="padding-bottom: 15px"
        :amount="amount"
        :save-payment-details="savePaymentDetails"
        @tokenized="payPalTokenized"
      />
      <p v-if="enableApplePay">
        <ApplePay
          v-if="showPayPalButton"
          style="padding-bottom: 15px"
          :responsive="true"
          :button-style="applePayButtonStyle"
          :amount="amount"
          @tokenized="applePayTokenized"
        />
      </p>
    </ClientOnly>
    <WithHostedFields
      ref="hostedFields"
      :include-number="includeNumber"
      :hosted-fields-styles="getHostedFieldsStyles"
      :host-dom-ready="braintreeSpecificDomReady"
    >
      <FormKit
        :id="formId"
        ref="creditCardForm"
        v-model="formValues"
        type="form"
        :actions="false"
        :name="name"
        class="edit-wrapper underline-form"
        @submit="submitForm()"
      >
        <slot
          name="nameForm"
          :validation-rules="{
            chNameNotEmail,
            chNameIsFull,
            chNameNotCardType,
          }"
        >
          <div class="row">
            <div :class="'columns ' + theme.cardholderName.classes">
              <FormKit
                name="cardholder_name"
                :placeholder="theme.showPlaceholders ? 'Cardholder Name*' : ''"
                label="Cardholder Name"
                validation="bail|required|chNameNotEmail|chNameIsFull|chNameNotCardType"
                :validation-rules="{
                  chNameNotEmail,
                  chNameIsFull,
                  chNameNotCardType,
                }"
                :validation-messages="{
                  chNameNotEmail:
                    'Error: It appears that you have entered an email address or other symbol. Please enter the first and last name of the Cardholder Name.',
                  chNameIsFull:
                    'Error: Please enter both a first and last name for your Cardholder Name.',
                  chNameNotCardType:
                    'Error: It appears that you have entered the type of credit card. Please enter the first and last name of the Cardholder Name.',
                }"
              />
            </div></div
        ></slot>
        <slot
          name="hostedRowForm"
          :tokenize-errors="tokenizeErrors"
          :include-number="includeNumber"
          :initial="initial"
        >
          <div class="row hosted-row">
            <div class="columns">
              <label>Card Number</label>
              <div v-if="includeNumber" id="id_card" class="hosted-field" />
              <div v-else id="id_card_masked" class="hosted-field card-masked">
                {{ initial.masked_number }}
              </div>
              <ul v-if="tokenizeErrors.number" class="formulate-input-errors">
                <li class="formulate-input-error">
                  Error: Invalid card number
                </li>
              </ul>
            </div>
          </div>
          <div class="row hosted-second-row">
            <div class="columns medium-4">
              <label>CVV</label>
              <div id="id_cvv" class="hosted-field" />
              <ul v-if="tokenizeErrors.cvv" class="formulate-input-errors">
                <li class="formulate-input-error">Error: Invalid CVV</li>
              </ul>
            </div>
            <div class="columns medium-4">
              <label>Expiration Month</label>
              <div id="id_exp_month" class="hosted-field hosted-select" />
              <ul
                v-if="tokenizeErrors.expirationMonth"
                class="formulate-input-errors"
              >
                <li class="formulate-input-error">
                  Error: Invalid expiration month
                </li>
              </ul>
            </div>
            <div class="columns medium-4">
              <label>Expiration Year</label>
              <div id="id_exp_year" class="hosted-field hosted-select" />
              <ul
                v-if="tokenizeErrors.expirationYear"
                class="formulate-input-errors"
              >
                <li class="formulate-input-error">
                  Error: Invalid expiration year
                </li>
              </ul>
            </div>
          </div>
        </slot>

        <template v-if="showBillingSameAsShippingCheckbox && shippingAddress">
          <div class="row checkbox-row-spacer">
            <div
              :class="
                'columns ' + customTheme.billingSameAsShippingCheckbox.classes
              "
            >
              <div class="input-group">
                <div class="checkbox">
                  <input
                    id="billingSameAsShipping"
                    v-model="billingSameAsShipping"
                    type="checkbox"
                  />
                  <label for="billingSameAsShipping" class="small-9"
                    >Billing address is same as shipping address</label
                  >
                </div>
              </div>
            </div>
          </div>
        </template>

        <slot
          name="addressForm"
          :state-options="stateOptions"
          :auto-complete-place-changed="autoCompletePlaceChanged"
        >
          <div class="row">
            <div :class="'columns ' + theme.billingAddress.line1.classes">
              <MapsAutocompleteAddressInputFK
                :id="addressLineOneId"
                name="billing1"
                autocomplete="address-line1"
                validation="required"
                :placeholder="
                  theme.showPlaceholders ? 'Street address, P.O. box, c/o' : ''
                "
                label="Address Line 1"
                @onAddressSelected="autoCompletePlaceChanged"
              />
            </div>
          </div>
          <div class="row">
            <div :class="'columns ' + theme.billingAddress.line2.classes">
              <FormKit
                name="billing2"
                :placeholder="
                  theme.showPlaceholders
                    ? 'Apartment, suite, unit, floor, etc'
                    : ''
                "
                label="Address Line 2"
              />
            </div>
          </div>
          <div class="row">
            <div :class="'columns ' + theme.billingAddress.city.classes">
              <FormKit
                name="city"
                :placeholder="theme.showPlaceholders ? 'City*' : ''"
                label="City"
                validation="required"
              />
            </div>
            <div :class="'columns ' + theme.billingAddress.state.classes">
              <FormKit
                :options="stateOptions"
                type="select"
                name="state"
                :placeholder="theme.showPlaceholders ? 'State*' : ''"
                label="State"
                validation="required"
              />
            </div>
            <div :class="'columns ' + theme.billingAddress.zip.classes">
              <FormKit
                name="zipcode"
                :placeholder="theme.showPlaceholders ? 'Zip Code *' : ''"
                label="Zip Code "
                validation="required"
              />
            </div>
          </div>
        </slot>
        <div v-if="showMakeDefaultCheckbox_" class="row">
          <div :class="'columns ' + theme.makeDefaultCheckbox.classes">
            <div class="input-group">
              <div class="checkbox">
                <input
                  id="cc-makeDefaultCheckbox"
                  v-model="makeDefault"
                  type="checkbox"
                />
                <label for="cc-makeDefaultCheckbox" class="small-8">
                  Make this my default choice for future orders
                </label>
              </div>
            </div>
          </div>
        </div>
        <template v-if="showSavePaymentDetailsCheckbox">
          <div class="row">
            <div
              :class="
                'columns ' + customTheme.savePaymentDetailsCheckbox.classes
              "
            >
              <div class="input-group">
                <div class="checkbox">
                  <input
                    id="savePaymentDetails"
                    v-model="savePaymentDetails"
                    type="checkbox"
                  />
                  <label for="savePaymentDetails" class="small-8"
                    >Save my Payment Details</label
                  >
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-if="saveError" class="row">
          <div class="columns">
            <div class="payment-errors form-error formulate-input-errors">
              Error: {{ saveError }}
            </div>
          </div>
        </div>
        <div v-if="!hideButtons" class="row account-buttons">
          <div class="columns text-center">
            <nuxt-link
              v-if="!hideCancel"
              :to="cancelHref"
              class="button button-outline"
              >Cancel</nuxt-link
            >
            <FormKit
              type="submit"
              actions="false"
              :label="submitButtonText"
              :disabled="submitting || disabled"
              :input-class="submitButtonClass"
            />
          </div>
        </div>
      </FormKit>
    </WithHostedFields>
    <slot name="after-form"></slot>
  </div>
</template>

<style scoped>
.checkbox-row-spacer {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.checkbox-row-spacer .columns.checkbox-group {
  padding-left: 0;
}
</style>
